/**
 * Centralised staff auth utilities.
 * Every outbound-email path in the agent/admin portals should use these so
 * we surface a clear "session expired" error instead of the raw 401 detail
 * ("Token invalid or expired") which agents mistook for a Resend outage.
 */

/** Read the currently-signed-in staff JWT from localStorage. */
export const getStaffToken = () => {
  try {
    const a = JSON.parse(localStorage.getItem('dhs-agent') || 'null') || JSON.parse(localStorage.getItem('dhs-admin') || 'null');
    return a?.token || null;
  } catch { return null; }
};

/** Auth headers for fetch() — empty object when no token so callers can spread safely. */
export const authHeaders = () => {
  const t = getStaffToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Clear the stale session and redirect to the correct login page.
 * `toast` is optional — if provided, we surface a friendly "session expired" message.
 */
export const handleAuthFailure = (toast) => {
  const hadAdmin = !!localStorage.getItem('dhs-admin');
  const hadAgent = !!localStorage.getItem('dhs-agent');
  localStorage.removeItem('dhs-agent');
  localStorage.removeItem('dhs-admin');
  if (typeof toast === 'function') {
    toast({
      title: 'Session expired',
      description: 'Please sign in again to continue — redirecting to the login page…',
    });
  }
  const to = hadAdmin ? '/admin/login' : hadAgent ? '/agent/login' : '/login';
  setTimeout(() => { window.location.href = to; }, 1500);
};

/**
 * Fetch wrapper that automatically handles 401 responses by triggering the
 * session-expired flow. Returns { res, json } for the caller to inspect
 * (or `null` if a 401 occurred, in which case the redirect is already in
 * flight and the caller should abort).
 */
export const authedFetch = async (url, init, toast) => {
  const res = await fetch(url, {
    ...init,
    headers: { ...(init?.headers || {}), ...authHeaders() },
  });
  if (res.status === 401) {
    handleAuthFailure(toast);
    return null;
  }
  let json = null;
  try { json = await res.json(); } catch { /* ignore non-JSON bodies */ }
  return { res, json };
};
